<template>
    <div v-if="plan">
        <card-header icon="fa-money-check-alt">
            <template #title>
                <span class="is-clickable copyable" @click="$copyText('R' + plan.id + ' - ' + plan.users.first_name + ' ' + plan.users.last_name + ' (' + plan.users.id + ')')">
                    R{{ plan.id }}, {{ plan.users.first_name }} {{ plan.users.last_name }}
                </span>
            </template>
        </card-header>

        <card-toolbar>
            <button class="icon-only" @click="$openCard('payment-plan-edit', {planId: plan.id}, card)" v-if="currentUser.isSuper"><i class="far fa-pencil"></i></button>
            <b-tooltip label="notes">
                <button class="icon-only" @click="$openCard('plan-notes', {planId: plan.id, userId: plan.users.id}, card)"><i class="far fa-notes"></i></button>
            </b-tooltip>
            <b-tooltip label="New Note">
                <button @click="$openCard('new-plan-note', {planId: plan.id, userId: plan.users.id}, card)"><i class="far fa-message-plus"></i>Note</button>
            </b-tooltip>
            <!--            <b-tooltip label="Offer" v-if="currentUser.isSuper">-->
            <!--                <button @click="$openCard('new-plan-offer', {planId: plan.id}, card)"><i class="far fa-envelope-open-dollar"></i>Offers</button>-->
            <!--            </b-tooltip>-->
            <b-tooltip label="New Transaction/Fee">
                <button v-if="currentUser.isSuper" @click="$openCard('new-plan-transaction', {planId: plan.id, userId: plan.user_id}, card)"><i class="far fa-dollar-circle"></i>New Transaction</button>
            </b-tooltip>
            <b-tooltip label="Offer" v-if="currentUser.isSuper && ((plan.expected_paid - plan.paid) > 0) && !plan.is_default && !plan.is_collections">
                <button @click="sendToDefault()"><i class="far fa-siren-on"></i>Send to Default</button>
            </b-tooltip>
            <b-tooltip label="Offer" v-if="currentUser.isSuper && plan.is_default && !plan.is_collections">
                <button @click="sendToCollections()"><i class="far fa-gavel"></i>Send to Collections</button>
            </b-tooltip>
            <b-tooltip label="Login to TPO" v-if="plan.has_tpo_account && currentUser.isSuper">
                <button class="icon-only" @click="tpoLogin()"><i class="far fa-arrow-right-to-arc"></i></button>
            </b-tooltip>
        </card-toolbar>

        <card-body>
            <card-list>

                <container-list-item
                    padded
                    class="status good pb-3 pl-5"
                    :class="{'deferred': isDeferred, 'default': plan.is_default, 'collections': plan.is_collections}
                ">

                    <div class="mb-2 has-text-weight-bold" v-if="plan.is_collections">Payment plan account is in collections.</div>
                    <div class="mb-2 has-text-weight-bold" v-else-if="plan.is_default">Payment plan account is in default.</div>
                    <div class="mb-2 has-text-weight-bold" v-else-if="isDeferred">Payment plan account is deferred until {{ plan.defer_on | moment('MMMM Do, YYYY') }}.</div>
                    <div class="mb-2 has-text-weight-bold" v-else>Payment plan account is active.</div>

                    <div class="mt-3 mb-3" v-if="(plan.expected_paid - plan.paid) > 0">
                        <i class="fas fa-exclamation-triangle has-text-warning mr-2"></i> This plan is past due {{ Math.max(0, (plan.expected_paid - plan.paid)) | currency }}.
                    </div>
                    <div class="mt-3 mb-3" v-if="plan.is_error">
                        <i class="fas fa-exclamation-triangle has-text-danger mr-2"></i> This plan is in error state with reason: {{ plan.last_error_msg }}.
                    </div>
                    <div class="mt-3 mb-3" v-if="!plan.active">
                        <i class="fas fa-exclamation-triangle has-text-danger mr-2"></i> This plan is suspended<span v-if="plan.inactive_reason"> with reason: {{ plan.inactive_reason }}</span>.
                    </div>

                    <b-select size="is-small" expanded v-model="plan.status" @input="updateStatus">
                        <option v-if="!plan.is_default && !plan.is_collections" value="active-good">In Good Standing</option>
                        <option v-if="!plan.is_default && !plan.is_collections" value="active-reminder">TPO Attempted Communication/Reminders</option>
                        <option v-if="!plan.is_default && !plan.is_collections" value="active-arrangement">User Made Arrangements</option>
                        <option v-if="!plan.is_default && !plan.is_collections" value="active-courtesy">TPO Provided Previous Courtesies</option>
                        <option v-if="!plan.is_default && !plan.is_collections" value="active-pending">Pending Default</option>
                        <option v-if="plan.is_default" value="default-new">In Default</option>
                        <option v-if="plan.is_default" value="default-reminder">TPO Attempted Communication/Reminders</option>
                        <option v-if="plan.is_default" value="default-arrangement">User Made Arrangements</option>
                        <option v-if="plan.is_default" value="default-pending">Pending Collections</option>
                        <option v-if="plan.is_collections" value="collections-new">In Collections (New)</option>
                        <option v-if="plan.is_collections" value="collections-ack">In Collections (Acknowledged)</option>
                        <option v-if="plan.is_collections" value="collections-hold">On Hold</option>
                        <option v-if="plan.is_collections" value="collections-info">More Information is Needed (See Notes)</option>
                        <option v-if="plan.is_collections" value="collections-contact-attempt">Collection Attorney Attempted Communications</option>
                        <option v-if="plan.is_collections" value="collections-contact">Collection Attorney Made Contact</option>
                        <option v-if="plan.is_collections" value="collections-deadline">Collection Attorney Created Deadline for Resolution</option>
                        <option v-if="plan.is_collections" value="collections-neg-agreement">Collection Attorney Negotiated Agreement in Place (Settled)</option>
                        <option v-if="plan.is_collections" value="collections-neg-modification">Collection Attorney Negotiated Payment Modification (Settled)</option>
                        <option v-if="plan.is_collections" value="collections-payment">Payment Received by Collection Attorney (Settled)</option>
                        <option v-if="plan.is_collections" value="collections-payment">Client is Paying Collection Attorney (Settled)</option>
                        <option v-if="plan.is_collections" value="collections-garnishment">Wage Garnishment in Place (Settled)</option>
                        <option v-if="plan.is_collections" value="collections-files">Collection Attorney Filed Lawsuit</option>
                        <option v-if="plan.is_collections" value="collections-summons">Summons Served</option>
                        <option v-if="plan.is_collections" value="collections-court">Collection Attorney Set Court Date</option>
                        <option v-if="plan.is_collections" value="collections-ruling">Collection Attorney Acquired Ruling</option>
                        <option v-if="plan.is_collections" value="collections-other">Other Status (See Notes)</option>
                    </b-select>
                    <div class="mt-1 is-size-7">Status as of {{ plan.status_updated_at | moment('MMMM Do, YYYY, h:mm:ss a') }} ({{ sinceStatusUpdate }} days ago)<span v-if="lastStatusUser"> by {{ lastStatusUser }}</span> <a @click="$openCard('plan-notes', {planId: plan.id, userId: plan.users.id}, card)"><i class="fad fa-notes ml-2"></i></a></div>
                </container-list-item>


                <data-row title="Total Due">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.total))">
                        {{ plan.total | currency }}
                    </span>
                </data-row>
                <data-row title="Total Paid">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.paid))">
                        {{ plan.paid | currency }}
                    </span>
                </data-row>
                <data-row title="Total Remaining">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.total - plan.paid))">
                        {{ plan.total - plan.paid  | currency }}
                    </span>
                </data-row>
                <data-row title="Past Due" v-if="(plan.expected_paid - plan.paid) > 0">
                    <i class="fad fa-exclamation-triangle has-text-warning mr-2"></i>
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(Math.max(0, (plan.expected_paid - plan.paid))))">
                        {{ Math.max(0, (plan.expected_paid - plan.paid)) | currency }}
                    </span>
                </data-row>
                <data-row title="Monthly">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.monthly))">
                        {{ plan.monthly  | currency }}
                    </span>
                </data-row>
                <data-row title="Term">
                    <span class="is-clickable copyable" @click="$copyText(plan.term + ' Months')">
                        {{ plan.term }} Months
                    </span>
                </data-row>

                <separator-list-item />

                <user-tile
                    :user="plan.users"
                    :card="card"
                    v-if="currentUser.isSuper"
                    @click="$openCard('user', {userId: plan.users.id}, card)"
                />
                <separator-list-item v-if="currentUser.isSuper" />

                <data-row title="Name">
                    <span class="is-clickable copyable" @click="$copyText(plan.users.first_name + ' ' + plan.users.last_name)">{{ plan.users.first_name }} {{ plan.users.last_name }}</span>
                </data-row>
                <data-row title="Maiden Name"><span class="is-clickable copyable" @click="$copyText(plan.users.maiden_name)">{{ plan.users.maiden_name }}</span></data-row>
                <data-row title="E-Mail Address"><span class="is-clickable copyable" @click="$copyText(plan.users.email)">{{ plan.users.email }}</span></data-row>
                <data-row title="Alt E-Mail Address"><span class="is-clickable copyable" @click="$copyText(plan.users.email_alt)">{{ plan.users.email_alt }}</span></data-row>
                <data-row title="Home Phone"><span class="is-clickable copyable" @click="$copyText(plan.users.phone)">{{ plan.users.phone | tel }}</span></data-row>
                <data-row title="Work Phone"><span class="is-clickable copyable" @click="$copyText(plan.users.phone_2)">{{ plan.users.phone_2 | tel }}</span></data-row>
                <data-row title="Address">
                    <div class="is-clickable copyable" @click="$copyText(plan.users.address + ((plan.users.address_2)?', '+plan.users.address_2:', ') + plan.users.city + ', ' + plan.users.state + ' ' + plan.users.zip)">
                        <div>{{ plan.users.address }}</div>
                        <div v-if="plan.users.address_2">{{ plan.users.address_2 }}</div>
                        <div>{{ plan.users.city }}, {{ plan.users.state }} &nbsp;{{ plan.users.zip }}</div>
                    </div>
                </data-row>
                <data-row title="Date of Birth"><span class="is-clickable copyable" @click="$copyText(plan.users.dob)">{{ plan.users.dob | moment('MM/DD/YYYY') }}</span></data-row>

                <separator-list-item />

                <navigation-list-item
                    title="Notes/Activity"
                    icon="fa-notes"
                    @click="$openCard('plan-notes', {planId: plan.id, userId: plan.users.id}, card)"
                    :active="(child && child.definition.component === 'plan-notes' && plan.id === parseInt(child.props.planId))"
                    :badge="plan.payment_plan_notes.length"
                />
                <navigation-list-item
                    title="User Requests/Actions"
                    icon="fa-message-check"
                    @click="$openCard('plan-requests', {planId: plan.id}, card)"
                    :active="(child && child.definition.component === 'plan-requests' && plan.id === parseInt(child.props.planId))"
                    :badge="plan.requests.length"
                />
                <navigation-list-item
                    title="Transactions"
                    icon="fa-usd-circle"
                    @click="$openCard('plan-transactions', {planId: plan.id}, card)"
                    :active="(child && child.definition.component === 'plan-transactions' && plan.id === parseInt(child.props.planId))"
                    :badge="plan.transactions.length"
                />
                <navigation-list-item
                    title="Communications"
                    icon="fa-envelopes-bulk"
                    @click="$openCard('plan-communications', {planId: plan.id}, card)"
                    :active="(child && child.definition.component === 'plan-communications' && plan.id === parseInt(child.props.planId))"
                    :badge="plan.communications.length"
                />
                <navigation-list-item
                    title="Agreement"
                    icon="fa-file-signature"
                    @click="openAgreement()"
                />
                <navigation-list-item
                    title="Default Documents"
                    icon="fa-print"
                    @click="openDocs()"
                />
                <navigation-list-item
                    title="Update ACH Payment Method"
                    icon="fa-university"
                    @click="$openCard('update-payment-method', {planId: plan.id}, card)"
                    :active="(child && child.definition.component === 'update-payment-method' && plan.id === parseInt(child.props.planId))"
                />
                <navigation-list-item
                    title="Create Tax Payment Plan"
                    icon="fa-receipt"
                    @click="$openCard('create-tax-plan', {planId: plan.id}, card)"
                    v-if="taxOwed > 0"
                />

                <subheader-list-item title="Plan Details" icon="fa-info-circle" />

                <data-row title="Nickname">{{ plan.nickname }}</data-row>
                <data-row title="Hours">{{ plan.hours }}</data-row>
                <data-row title="Courses Expire">{{ calculated.coursesExpire | moment('MMMM Do, YYYY') }}</data-row>
                <data-row title="Reseller" v-if="plan.portal_users">{{ plan.portal_users.first_name }} {{ plan.portal_users.last_name }}</data-row>
                <data-row title="Agreement Total">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.agreement_total))">
                        {{ plan.agreement_total | currency }}
                    </span>
                </data-row>
                <data-row title="Total Due">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.total))">
                        {{ plan.total | currency }}
                    </span>
                </data-row>
                <data-row title="Total Paid">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.paid))">
                        {{ plan.paid | currency }}
                    </span>
                </data-row>
                <data-row title="Total Remaining">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.total - plan.paid))">
                        {{ plan.total - plan.paid  | currency }}
                    </span>
                </data-row>
                <data-row title="Monthly">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.monthly))">
                        {{ plan.monthly  | currency }}
                    </span>
                </data-row>
                <data-row title="Term">
                    <span class="is-clickable copyable" @click="$copyText(plan.term + ' Months')">
                        {{ plan.term }} Months
                    </span>
                </data-row>
                <data-row title="Expected Paid">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.expected_paid))">
                        {{ plan.expected_paid | currency }}
                    </span>
                </data-row>
                <data-row title="Expected Paid Offset" v-if="plan.expected_paid_offset > 0">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.expected_paid_offset))">
                        {{ plan.expected_paid_offset | currency }}
                    </span>
                </data-row>
                <data-row title="Past Due">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(Math.max(0, (plan.expected_paid - plan.paid))))">
                        {{ Math.max(0, (plan.expected_paid - plan.paid)) | currency }}
                    </span>
                </data-row>
                <data-row title="Interest">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.interest_incurred))">
                        {{ plan.interest_incurred | currency }}
                    </span>
                </data-row>
                <data-row title="Fees">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.fees_incurred))">
                        {{ plan.fees_incurred | currency }}
                    </span>
                </data-row>
                <data-row title="TPO Plan"><i class="fas fa-check" v-if="plan.is_tpo"></i><i class="fas fa-ban" v-else></i></data-row>
                <data-row title="TPO Complete"><i class="fas fa-check" v-if="plan.is_tpo_complete"></i><i class="fas fa-ban" v-else></i></data-row>
                <data-row title="Active"><i class="fas fa-check" v-if="plan.active"></i><i class="fas fa-ban" v-else></i></data-row>
                <data-row title="Inactive Reason">{{ plan.inactive_reason }}</data-row>
                <data-row title="In Error State"><i class="fas fa-exclamation-triangle has-text-danger" v-if="plan.is_error"></i><i class="fas fa-ban has-text-grey" v-else></i></data-row>
                <data-row title="Last Error">{{ plan.last_error_msg }}</data-row>
                <data-row title="In Default"><i class="fas fa-exclamation-triangle has-text-danger" v-if="plan.is_default"></i><i class="fas fa-ban has-text-grey" v-else></i></data-row>
                <data-row title="In Collections"><i class="fas fa-exclamation-triangle has-text-danger" v-if="plan.is_collections"></i><i class="fas fa-ban has-text-grey" v-else></i></data-row>
                <data-row title="Payment Date">1st of Month</data-row>
                <data-row title="Deferment">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.moment(plan.defer_on, 'MMMM Do, YYYY'))">
                        {{ plan.defer_on | moment('MMMM Do, YYYY') }}
                    </span>
                </data-row>
                <data-row title="Down Payment">{{ plan.down | currency }}</data-row>
                <data-row title="Payments Made">{{ plan.payments_made }}</data-row>
                <data-row title="Last Paid Month">
                    <div v-if="plan.last_paid_month_on">{{ plan.last_paid_month_on | moment('MMMM YYYY') }}</div>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Last Attempt">
                    <div v-if="plan.last_attempted_at">{{ plan.last_attempted_at | moment('MMMM Do, YYYY h:mm:ss a') }}</div>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Last Successful">
                    <div v-if="plan.last_successful_at">{{ plan.last_successful_at | moment('MMMM Do, YYYY h:mm:ss a') }}</div>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Created">
                    <div v-if="plan.created_at">{{ plan.created_at | moment('MMMM Do, YYYY h:mm:ss a') }}</div>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Updated">
                    <div v-if="plan.updated_at">{{ plan.updated_at | moment('MMMM Do, YYYY h:mm:ss a') }}</div>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>

                <separator-list-item />

                <subheader-list-item title="Sales Tax" icon="fa-receipt" />

                <data-row title="Base Amount">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.base_amount))" v-if="plan.base_amount">
                        {{ plan.base_amount | currency }}
                    </span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Tax Amount">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.tax_amount))" v-if="plan.tax_amount">
                        {{ plan.tax_amount | currency }}
                    </span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Tax Paid">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.tax_paid))" v-if="plan.tax_paid">
                        {{ plan.tax_paid | currency }}
                    </span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Tax Owed">
                    <span class="is-clickable copyable" v-if="taxOwed > 0" @click="$copyText($options.filters.currency(taxOwed))">
                        {{ taxOwed | currency }}
                    </span>
                    <div class="text-muted" v-else>$0.00</div>
                </data-row>
                <data-row title="Tax Remitted">
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(plan.tax_remitted))" v-if="plan.tax_remitted">
                        {{ plan.tax_remitted | currency }}
                    </span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Tax Rate">
                    <span v-if="plan.tax_rate">{{ (plan.tax_rate * 100).toFixed(3) }}%</span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="State Rate">
                    <span v-if="plan.tax_state_rate">{{ (plan.tax_state_rate * 100).toFixed(3) }}%</span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Local Rate">
                    <span v-if="plan.tax_local_rate">{{ (plan.tax_local_rate * 100).toFixed(3) }}%</span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Jurisdiction">
                    <span v-if="plan.tax_jurisdiction">{{ plan.tax_jurisdiction }}</span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Location Code">
                    <span v-if="plan.tax_location_code">{{ plan.tax_location_code }}</span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Tax Calculated">
                    <div v-if="plan.tax_calculated_at">{{ plan.tax_calculated_at | moment('MMMM Do, YYYY h:mm:ss a') }}</div>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>

                <separator-list-item />

                <subheader-list-item title="Processor Information" icon="fa-credit-card" />

                <data-row title="Stax Customer ID">
                    <span class="is-clickable copyable" @click="$copyText(plan.users.fatt_customer_id)" v-if="plan.users.fatt_customer_id">
                        {{ plan.users.fatt_customer_id }}
                    </span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Stax Invoice ID">
                    <span class="is-clickable copyable" @click="$copyText(plan.fatt_invoice_id)" v-if="plan.fatt_invoice_id">
                        {{ plan.fatt_invoice_id }}
                    </span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Payment ID" v-if="plan.payment_methods">
                    <span class="is-clickable copyable" @click="$copyText(plan.payment_methods.id)" v-if="plan.payment_methods.id">
                        {{ plan.payment_methods.id }}
                    </span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Stax Payment ID" v-if="plan.payment_methods">
                    <span class="is-clickable copyable" @click="$copyText(plan.payment_methods.fatt_method_id)" v-if="plan.payment_methods.fatt_method_id">
                        {{ plan.payment_methods.fatt_method_id }}
                    </span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="PaymentType" v-if="plan.payment_methods">
                    <span v-if="plan.payment_methods.type">{{ plan.payment_methods.type }}</span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Payment Name" v-if="plan.payment_methods">
                    <span v-if="plan.payment_methods.nickname">{{ plan.payment_methods.nickname }}</span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>
                <data-row title="Last Four" v-if="plan.payment_methods">
                    <span v-if="plan.payment_methods.last_four">{{ plan.payment_methods.last_four }}</span>
                    <div class="text-muted" v-else>N/A</div>
                </data-row>

                <separator-list-item />

                <subheader-list-item title="Calculations" icon="fa-calculator" />

                <data-row title="Defer Days">
                    <div class="is-size-7 has-text-grey">Number of days payment was deferred since sign date.</div>
                    <span class="is-clickable copyable" @click="$copyText(calculated.deferDays)">
                        {{ calculated.deferDays }}
                    </span>
                </data-row>
                <data-row title="Term Days">
                    <div class="is-size-7 has-text-grey">Number of days in the full term of agreement.</div>
                    <span class="is-clickable copyable" @click="$copyText(calculated.deferDays)">
                        {{ calculated.deferDays }}
                    </span>
                </data-row>
                <data-row title="Active Days">
                    <div class="is-size-7 has-text-grey">Number of days into active plan, since deferment.</div>
                    <span class="is-clickable copyable" @click="$copyText(calculated.activeDays)">
                        {{ calculated.activeDays }}
                    </span>
                </data-row>
                <data-row title="Defer Interest">
                    <div class="is-size-7 has-text-grey">Interest of deferment period.</div>
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(calculated.deferInterest))">
                        {{ calculated.deferInterest | currency }}
                    </span>
                </data-row>
                <data-row title="Term Interest">
                    <div class="is-size-7 has-text-grey">Interest of full agreement term.</div>
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(calculated.termInterest))">
                        {{ calculated.termInterest | currency }}
                    </span>
                </data-row>
                <data-row title="Active Interest">
                    <div class="is-size-7 has-text-grey">Interest of active plan days.</div>
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(calculated.activeInterest))">
                        {{ calculated.activeInterest | currency }}
                    </span>
                </data-row>
                <data-row title="Full Interest">
                    <div class="is-size-7 has-text-grey">Total Interest per agreement (Defer Interest + Term Interest).</div>
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(calculated.fullInterest))">
                        {{ calculated.fullInterest | currency }}
                    </span>
                </data-row>
                <data-row title="Payoff Interest">
                    <div class="is-size-7 has-text-grey">Interest if doing full payoff (Defer Interest + Active Interest)</div>
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(calculated.payoffInterest))">
                        {{ calculated.payoffInterest | currency }}
                    </span>
                </data-row>
                <data-row title="Payoff Amount">
                    <div class="is-size-7 has-text-grey">Total Amount due if doing a full payoff.</div>
                    <span class="is-clickable copyable" @click="$copyText($options.filters.currency(calculated.payoffAmount))">
                        {{ calculated.payoffAmount | currency }}
                    </span>
                </data-row>

            </card-list>
        </card-body>

        <card-footer class="has-text-right">
            <span class="is-clickable copyable" @click="$copyText(plan.id)"><i class="fal fa-barcode-alt mr-2"></i>{{ plan.id }}</span>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import NavigationListItem from "@/TIER/components/NavigationListItem";
    import CardContainer from "@/TIER/components/CardContainer";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardFooter from "@/TIER/components/CardFooter";
    import SeparatorListItem from "@/TIER/components/SeparatorListItem";

    import {client as http} from '@/http_client';
    import async from "async";
    import DataRow from "../../TIER/components/DataRow";
    import UserTile from "../../components/tiles/UserTile";

    import {mapGetters} from "vuex";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem.vue";

    import moment from "moment";
    import numeral from "numeral";
    import ContainerListItem from "../../TIER/components/ContainerListItem.vue";

    export default {
        components: {ContainerListItem, SubheaderListItem, UserTile, DataRow, SeparatorListItem, CardFooter, CardToolbar, CardContainer, NavigationListItem, CardList, CardBody, CardHeader},
        props: ['card', 'props', 'child'],
        data() {
            return {
                plan: null
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            calculated: function () {
                let deferDays = Math.max(0, moment(this.plan.defer_on).diff(moment(this.plan.created_at), 'days'));
                let termDays = Math.max(0, moment(this.plan.defer_on).add(this.plan.term - 1, 'months').diff(moment(this.plan.defer_on), 'days'));
                let activeDays = Math.max(0, moment().diff(moment(this.plan.defer_on), 'days'));
                let deferInterest = (deferDays) * 0.0005 * this.plan.agreement_total;
                let termInterest = (termDays) * 0.0005 * this.plan.agreement_total;
                let activeInterest = (activeDays) * 0.0005 * this.plan.agreement_total;
                let fullInterest = (deferDays + termDays) * 0.0005 * this.plan.agreement_total;
                let payoffInterest = (deferDays + activeDays) * 0.0005 * this.plan.agreement_total;
                let payoffAmount = this.plan.agreement_total - this.plan.paid + ((deferDays + activeDays) * 0.0005 * this.plan.agreement_total);
                let coursesExpire = this.plan.is_tpo ? moment(this.plan.created_at).add(3, 'years') : moment(this.plan.created_at).endOf('month').add(3, 'years');

                return {
                    deferDays,
                    termDays,
                    activeDays,
                    deferInterest,
                    termInterest,
                    activeInterest,
                    fullInterest,
                    payoffInterest,
                    payoffAmount,
                    coursesExpire
                };
            },
            sinceStatusUpdate: function () {
                return numeral(moment().diff(this.plan.status_updated_at, 'days')).format('0,0');
            },
            lastStatusUser: function () {
                if (this.plan.payment_plan_notes.length === 0) return null;
                let lastStatus = this.plan.payment_plan_notes[0]
                return lastStatus.portal_users.first_name + ' ' + lastStatus.portal_users.last_name;
            },
            isDeferred: function () {
                return moment().isBefore(this.plan.defer_on);
            },
            taxOwed: function () {
                return (parseFloat(this.plan.tax_amount) || 0) - (parseFloat(this.plan.tax_paid) || 0);
            }
        },
        methods: {
            loadPlan: function () {
                this.$emit('loading', true);

                async.series([
                    (cb) => {
                        http.get('payment-plans/' + this.props.planId, {force: true}).then(response => {
                            this.plan = response.data;
                            this.plan.defer_on = moment.utc(this.plan.defer_on);
                            this.plan.last_paid_month_on = moment.utc(this.plan.last_paid_month_on);
                            cb();
                        }).catch(err => {
                            cb(err);
                        });
                    }
                ], err => {
                    this.$emit('loading', false);
                });
            },
            updateStatus: function () {
                this.$emit('loading', true);

                http.post('payment-plans/' + this.plan.id + '/status', {status: this.plan.status}).then(response => {
                    this.plan.status_updated_at = moment().toDate();
                    this.$emit('loading', false);
                }).catch(err => {
                    this.$emit('loading', false);
                });
            },
            sendToDefault: function () {
                this.$emit('loading', true);
                if (window.confirm('Are you sure you want to send this plan to default? Interest will be added and notification will be sent to user.')) {
                    http.post('payment-plans/' + this.plan.id + '/default').then(response => {
                        this.loadPlan();
                        this.$reloadCard('plans-list');
                    }).catch(err => {
                        this.$emit('loading', false);
                    });
                }
            },
            sendToCollections: function () {
                this.$emit('loading', true);
                if (window.confirm('Are you sure you want to send this plan to collections?')) {
                    http.post('payment-plans/' + this.plan.id + '/collections').then(response => {
                        this.loadPlan();
                        this.$reloadCard('plans-list');
                    }).catch(err => {
                        this.$emit('loading', false);
                    });
                }
            },
            reload: function () {
                this.loadPlan();
            },
            tpoLogin: function () {
                http.post('payment-plans/' + this.plan.id + '/tpo-login').then(response => {
                    window.open('https://app.thepaymentoption.com/#/accounts/' + response.data.session, '_blank');
                });
            },
            openAgreement: function () {
                window.open('https://app.thepaymentoption.com/api/scripts/agreement/' + this.plan.id, '_blank');
            },
            openDocs: function () {
                window.open('https://app.thepaymentoption.com/api/scripts/default/' + this.plan.id, '_blank');
            }
        },
        watch: {
            'props.planId': function () {
                this.loadPlan();
            }
        },
        mounted() {
            this.loadPlan();
        }
    };
</script>

<style>
    .fa-check {
        color: green;
    }

    .fa-ban {
        color: red;
    }

    .copyable:hover {
        background-color: rgba(30, 56, 92, 0.1);
    }
</style>

<style lang="scss" scoped>
    .status {
        border-style: solid;
        border-width: 3px 0 3px 0 !important;

        &.good, &good:hover {
            background-color: #f8fff6 !important;
            border-color: #008000 !important;
        }

        &.deferred, &deferred:hover {
            background-color: #fffff0 !important;
            border-color: #ffe261 !important;
        }

        &.default, &default:hover {
            background-color: #fff1df !important;
            border-color: #cd8111 !important;
        }

        &.collections, &collections:hover {
            background-color: #fff9f9 !important;
            border-color: #9b0d0d !important;
        }
    }

</style>
